import moment from "moment";
import { supabase } from "./supabaseClient";

const PREVIEW_ROLE_LABELS = new Set(["Owner", "Super Admin"]);
const VIEW_AS_STUDENT_KEY = "lms_view_as_student";

/** Persist selected student so Profile (and other pages) can use the same preview target. */
export const getStoredViewAsStudent = () => {
  try {
    const raw = sessionStorage.getItem(VIEW_AS_STUDENT_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed?.value) return null;
    return {
      value: parsed.value,
      userId: parsed.userId || null,
      email: parsed.email || null,
      name: parsed.name || parsed.label || "Student",
    };
  } catch {
    return null;
  }
};

export const setStoredViewAsStudent = (student) => {
  try {
    if (!student) {
      sessionStorage.removeItem(VIEW_AS_STUDENT_KEY);
      return;
    }
    sessionStorage.setItem(
      VIEW_AS_STUDENT_KEY,
      JSON.stringify({
        value: student.value,
        userId: student.userId || null,
        email: student.email || null,
        name: student.name || student.label || "Student",
      })
    );
  } catch (e) {
    console.error("Failed to store view-as-student preview:", e);
  }
};

export const clearStoredViewAsStudent = () => {
  try {
    sessionStorage.removeItem(VIEW_AS_STUDENT_KEY);
  } catch {
    /* ignore */
  }
};

/**
 * Returns whether the logged-in user is active staff with Owner or Super Admin privilege.
 * Prefer roles.label (canonical tier); fall back to roles.name when label is empty
 * so tenants that never filled labels still work.
 * Does not write any data — read-only check against staff + roles.
 */
export const getStaffPreviewAccess = async (userId) => {
  if (!userId) {
    return { canPreview: false, staff: null, roleLabel: null };
  }

  const { data, error } = await supabase
    .from("staff")
    .select("id, name, email, status, user_id, roles (id, name, label)")
    .eq("user_id", userId)
    .eq("status", "active")
    .maybeSingle();

  if (error || !data) {
    if (error) console.error("Error checking staff preview access:", error);
    return { canPreview: false, staff: null, roleLabel: null };
  }

  const roleLabel = (data.roles?.label || data.roles?.name || "").trim() || null;
  return {
    canPreview: PREVIEW_ROLE_LABELS.has(roleLabel),
    staff: data,
    roleLabel,
  };
};

/** Compact date range for dropdown rows. Fallback: null if dates missing/invalid. */
export const formatCertRunDateRange = (startDate, endDate) => {
  const start = moment(startDate);
  const end = moment(endDate);
  if (!start.isValid() || !end.isValid()) return null;
  if (start.year() === end.year()) {
    return `${start.format("D MMM")} – ${end.format("D MMM YYYY")}`;
  }
  return `${start.format("D MMM YYYY")} – ${end.format("D MMM YYYY")}`;
};

/**
 * Run timing status relative to today.
 * - upcoming: "in 1 day" / "in X days"
 * - in_progress: "1 day in" / "X days in"
 * - completed: "Completed"
 * Fallback: unknown when dates are missing.
 */
export const getCertRunStatus = (startDate, endDate, now = moment()) => {
  const today = moment(now).startOf("day");
  const start = moment(startDate).startOf("day");
  const end = moment(endDate).startOf("day");

  if (!start.isValid() || !end.isValid()) {
    return { kind: "unknown", label: null };
  }
  if (today.isAfter(end)) {
    return { kind: "completed", label: "Completed" };
  }
  if (today.isBefore(start)) {
    const n = start.diff(today, "days");
    return {
      kind: "upcoming",
      label: n === 1 ? "in 1 day" : `in ${n} days`,
    };
  }
  const n = today.diff(start, "days") + 1;
  return {
    kind: "in_progress",
    label: n === 1 ? "1 day in" : `${n} days in`,
  };
};

/**
 * Prefer in-progress, then soonest upcoming, then most recently completed.
 * Fallback: first run with a code if dates are incomplete.
 */
const pickBestCertRun = (runs) => {
  if (!runs?.length) return null;
  const today = moment().startOf("day");

  const scored = runs.map((run) => {
    const start = moment(run.startDate).startOf("day");
    const end = moment(run.endDate).startOf("day");
    if (!start.isValid() || !end.isValid()) {
      return { run, tier: 3, tie: 0 };
    }
    if (today.isBetween(start, end, "day", "[]")) {
      return { run, tier: 0, tie: start.valueOf() };
    }
    if (today.isBefore(start)) {
      return { run, tier: 1, tie: -start.valueOf() };
    }
    return { run, tier: 2, tie: end.valueOf() };
  });

  scored.sort((a, b) => a.tier - b.tier || b.tie - a.tie);
  return scored[0].run;
};

/**
 * Distinct students who have at least one paid enrollment (full-cert or standalone).
 * One row per student; shows their most relevant programme/run.
 */
export const fetchEnrolledStudentsForPreview = async () => {
  const { data: certRows, error: certError } = await supabase
    .from("registration")
    .select(
      "id, user_id, participant_email, participant_name, draft_cert_run_code, created_at, full_certification_runs (id, start_date, end_date, full_certification (name))"
    )
    .eq("registration_stage", "payment_received")
    .not("draft_cert_run_code", "is", null)
    .order("participant_name", { ascending: true });

  if (certError) {
    console.error("Error fetching cert students for preview:", certError);
  }

  const { data: standaloneRows, error: standaloneError } = await supabase
    .from("registration")
    .select(
      "id, user_id, participant_email, participant_name, draft_course_run_code, created_at, course_run:draft_course_run_code (id, start_date, end_date, course_type (name))"
    )
    .eq("registration_stage", "payment_received")
    .not("draft_course_run_code", "is", null)
    .is("draft_cert_run_code", null)
    .order("participant_name", { ascending: true });

  if (standaloneError) {
    console.error("Error fetching standalone students for preview:", standaloneError);
  }

  const byKey = new Map();

  const upsertStudent = (row, runEntry) => {
    const email = (row.participant_email || "").trim().toLowerCase();
    const key = email || row.user_id;
    if (!key) return;

    const existing = byKey.get(key);
    if (existing) {
      if (!existing.userId && row.user_id) {
        existing.userId = row.user_id;
        existing.value = row.user_id;
      }
      if (runEntry) {
        const already = existing.runs.some((r) => r.runCode === runEntry.runCode);
        if (!already) existing.runs.push(runEntry);
      }
      return;
    }

    const name = (row.participant_name || "").trim() || email || "Unknown student";
    byKey.set(key, {
      userId: row.user_id || null,
      email: row.participant_email || null,
      name,
      value: row.user_id || key,
      runs: runEntry ? [runEntry] : [],
    });
  };

  (certRows || []).forEach((row) => {
    const run = row.full_certification_runs;
    const programmeName = (run?.full_certification?.name || "").trim() || null;
    const runEntry = run?.id
      ? {
          runCode: run.id,
          startDate: run.start_date || null,
          endDate: run.end_date || null,
          programmeName,
        }
      : row.draft_cert_run_code
        ? {
            runCode: row.draft_cert_run_code,
            startDate: null,
            endDate: null,
            programmeName: null,
          }
        : null;
    upsertStudent(row, runEntry);
  });

  (standaloneRows || []).forEach((row) => {
    const run = row.course_run;
    const programmeName = (run?.course_type?.name || "").trim() || null;
    const runEntry = run?.id
      ? {
          runCode: run.id,
          startDate: run.start_date || null,
          endDate: run.end_date || null,
          programmeName,
        }
      : row.draft_course_run_code
        ? {
            runCode: row.draft_course_run_code,
            startDate: null,
            endDate: null,
            programmeName: null,
          }
        : null;
    upsertStudent(row, runEntry);
  });

  return Array.from(byKey.values())
    .map((student) => {
      const best = pickBestCertRun(student.runs);
      const status = getCertRunStatus(best?.startDate, best?.endDate);
      const dateRangeLabel = formatCertRunDateRange(best?.startDate, best?.endDate);
      const runCode = best?.runCode || null;
      const programmeName = best?.programmeName || null;
      const metaParts = [dateRangeLabel, runCode, programmeName].filter(Boolean);

      return {
        userId: student.userId,
        email: student.email,
        name: student.name,
        value: student.value,
        runCode,
        programmeName,
        startDate: best?.startDate || null,
        endDate: best?.endDate || null,
        dateRangeLabel,
        statusKind: status.kind,
        statusLabel: status.label,
        label: student.name,
        searchText: [student.name, student.email].filter(Boolean).join(" "),
        secondaryLabel: metaParts.join(" · "),
      };
    })
    .sort((a, b) => a.name.localeCompare(b.name, undefined, { sensitivity: "base" }));
};
